import {
  COVERAGE_PLAN_TYPE_EMPLOYER,
  HEALTH_COVER_MEDICARE_ORIGINAL,
} from '../config/InsuranceProviders';
import { StoreStateInternal } from '../store/Store';
import { getReferralPaymentRoute, getScreenAnswers } from './ConditionalNextScreenSelectors';
import {
  hasOptedForSelfPay,
  hasOriginalMedicareAnswer,
  isExcludedFromMedicareAccess,
  isMedicareOriginal,
  isUsNon65Plus,
} from './MedicareAccess';

/**
 * Routes the insurance-provider dropdown into the under-65 coverage flow. Original Medicare skips
 * the plan-type question straight to the address (the dropdown answer already carries the type);
 * every other US non-65+ user answers the plan-type question. Non-US users, and US users who chose
 * "I'll pay for Nerva myself", get `undefined` so the config's existing exit applies.
 */
export function getCoverageEntryRoute(
  state: StoreStateInternal,
  screenName: string
): 'medicareOriginal' | 'collectCoverage' | undefined {
  if (!isUsNon65Plus(state) || hasOptedForSelfPay(state)) return undefined;
  const selection = getScreenAnswers(state, screenName)?.[0];
  return selection === HEALTH_COVER_MEDICARE_ORIGINAL ? 'medicareOriginal' : 'collectCoverage';
}

/**
 * Referred-flow variant of {@link getCoverageEntryRoute}: users outside the coverage audience keep
 * their referral route (access code / ASL / PAP / Stripe payment) exactly as the dropdown did before.
 */
export function getReferredCoverageEntryRoute(
  state: StoreStateInternal,
  screenName: string
): ReturnType<typeof getCoverageEntryRoute> | ReturnType<typeof getReferralPaymentRoute> {
  return getCoverageEntryRoute(state, screenName) ?? getReferralPaymentRoute(state);
}

/**
 * Routes the benefits screen by how the user arrived: the employer path continues to the work-email
 * ask; self-purchased and not-sure go straight to the address.
 */
export function getCoverageBenefitsRoute(state: StoreStateInternal): 'employer' | undefined {
  return state.userData.coveragePlanType?.[0] === COVERAGE_PLAN_TYPE_EMPLOYER
    ? 'employer'
    : undefined;
}

/**
 * Routes the address screen. Its state decides ACCESS for Original Medicare, so an Original Medicare
 * user who can't find their address is asked for their state instead; everyone else goes to results.
 */
export function getCoverageAddressRoute(state: StoreStateInternal): 'askState' | undefined {
  return state.userData.coverageAddressNotFound?.[0] === 'Yes' && hasOriginalMedicareAnswer(state)
    ? 'askState'
    : undefined;
}

/**
 * Routes the Control results screen. Original Medicare (dropdown or plan-type answer, not self-pay)
 * enters the ACCESS pathway at the disclosure, as MaintenanceExperience routed under-65 Original
 * Medicare before the coverage flow moved after sign-up; everyone else falls through to payment.
 */
export function getCoverageResultsRoute(state: StoreStateInternal): 'medicareOriginal' | undefined {
  return isMedicareOriginal(state) ? 'medicareOriginal' : undefined;
}

/**
 * Routes the referred results placeholder. Original Medicare users (not clinically excluded) enter
 * the ACCESS pathway at the MBI screen, as the dropdown routed them before the coverage flow existed;
 * everyone else re-resolves their referral/payment route.
 */
export function getReferredCoverageResultsRoute(
  state: StoreStateInternal
): 'medicareOriginal' | ReturnType<typeof getReferralPaymentRoute> {
  if (isMedicareOriginal(state) && !isExcludedFromMedicareAccess(state)) return 'medicareOriginal';
  return getReferralPaymentRoute(state);
}
